import type { RunSecret, RunSecretKind } from '@deskworks/shared-types';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation, useMe, useRunSecrets, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const NAME = /^[A-Z_][A-Z0-9_]*$/;
const KEY = /^[A-Z0-9_]+$/;

/** What a credential holds (docs/features/secrets.md#kinds). */
const KINDS: Record<RunSecretKind, string> = {
  text: t('Value'),
  key_value: t('Key-value pairs'),
  json: t('JSON'),
};

type Pair = { key: string; value: string };

function jsonError(text: string) {
  if (!text.trim()) return null;
  try {
    JSON.parse(text);
    return null;
  } catch (err) {
    return t('No valid JSON: {error}', { error: (err as Error).message });
  }
}

/** Where a secret is usable, as for runtimes: this workspace only, every workspace of the account, or the chosen ones. */
type Availability = 'workspace' | 'account' | 'selected';

function availabilityOf(secret: RunSecret | undefined): Availability {
  if (!secret || secret.workspaceId) return 'workspace';
  return secret.sharedWorkspaceIds ? 'selected' : 'account';
}

/** A shared secret shows in other workspaces' lists too. */
const invalidateSecrets = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'ws' && q.queryKey[2] === 'secrets' });

/** Account owners and admins share secrets and connections and change shared secrets. */
export function useAccountAdmin(workspaceId: string) {
  const workspace = useWorkspace(workspaceId);
  const me = useMe();
  const role = me.data?.accounts.find((a) => a.id === workspace.data?.accountId)?.role;
  return role === 'owner' || role === 'admin';
}

/** The workspace's secrets (docs/features/secrets.md): values a desk gives its runs as environment variables. */
export function WorkspaceSecretsSection({ workspaceId }: { workspaceId: string }) {
  const secrets = useRunSecrets(workspaceId);
  const accountAdmin = useAccountAdmin(workspaceId);
  const queryClient = useQueryClient();
  const remove = useApiMutation((id: string) => api.secrets.remove(id), {
    success: t('Credential deleted'),
    onSuccess: () => void invalidateSecrets(queryClient),
  });

  return (
    <Section
      className="mt-6"
      icon={<KeyRound />}
      title={t('Credentials')}
      description={t('Values like API keys, logins or a service account’s JSON that a script in a run needs. A desk gives a credential to its agents; their runs get it as environment variables, the agent only sees the names.')}
      actions={
        <SecretDialog workspaceId={workspaceId} canShare={accountAdmin}>
          <Button variant="outline" size="sm">
            <Plus /> {t('Add credential')}
          </Button>
        </SecretDialog>
      }
    >
      {secrets.isPending ? (
        <LoadingRows rows={2} />
      ) : secrets.isError ? (
        <ErrorState error={secrets.error} onRetry={() => void secrets.refetch()} />
      ) : secrets.data.length === 0 ? (
        <EmptyState compact title={t('No credentials yet')} description={t('Add one, e.g. OPENAI_API_KEY, and give it to a desk.')} />
      ) : (
        <ul className="-mx-5 divide-y border-y">
          {secrets.data.map((s) => {
            const editable = s.workspaceId !== null || accountAdmin;
            return (
              <li key={s.id} className="flex items-center gap-3 px-5 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-mono text-sm">{s.name}</span>
                    {s.kind !== 'text' && (
                      <Badge tone="outline" title={s.kind === 'key_value' ? s.keys.map((k) => `${s.name}_${k}`).join(', ') : `${s.name}, ${s.name}_FILE`}>
                        {s.kind === 'key_value' ? t('{n} keys', { n: s.keys.length }) : KINDS.json}
                      </Badge>
                    )}
                    {s.workspaceId === null && (
                      <Badge tone="outline" title={accountAdmin ? undefined : t('Shared by the account – only account admins can change it')}>
                        {s.sharedWorkspaceIds ? t('{n} workspaces', { n: s.sharedWorkspaceIds.length }) : t('All workspaces')}
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {s.desks.length === 0 ? t('Not given to any desk') : t('Desks: {names}', { names: s.desks.map((d) => d.name).join(', ') })}
                    {s.description && ` · ${s.description}`}
                  </div>
                </div>
                {editable && (
                  <>
                    <SecretDialog workspaceId={workspaceId} secret={s} canShare={accountAdmin}>
                      <Button variant="ghost" size="icon-sm" aria-label={t('Edit')} title={t('Edit')}>
                        <Pencil />
                      </Button>
                    </SecretDialog>
                    <Confirm
                      title={t('Delete credential “{name}”?', { name: s.name })}
                      description={
                        s.workspaceId === null
                          ? t('The value is deleted and no desk’s runs get it any more, in every workspace it is shared with.')
                          : t('The value is deleted and no desk’s runs get it any more.')
                      }
                      confirmLabel={t('Delete')}
                      destructive
                      onConfirm={() => remove.mutateAsync(s.id)}
                    >
                      <Button variant="ghost" size="icon-sm" className="text-destructive hover:text-destructive" aria-label={t('Delete')} title={t('Delete')}>
                        <Trash2 />
                      </Button>
                    </Confirm>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function SecretDialog({ workspaceId, secret, canShare, children }: { workspaceId: string; secret?: RunSecret; canShare: boolean; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [kind, setKind] = useState<RunSecretKind>('text');
  const [pairs, setPairs] = useState<Pair[]>([{ key: '', value: '' }]);
  const [availability, setAvailability] = useState<Availability>('workspace');
  const [sharedWith, setSharedWith] = useState<string[]>([workspaceId]);

  useEffect(() => {
    if (!open) return;
    setName(secret?.name ?? '');
    setDescription(secret?.description ?? '');
    setValue('');
    setKind(secret?.kind ?? 'text');
    setPairs(secret?.kind === 'key_value' ? secret.keys.map((key) => ({ key, value: '' })) : [{ key: '', value: '' }]);
    setAvailability(availabilityOf(secret));
    setSharedWith(secret?.sharedWorkspaceIds ?? [secret?.workspaceId ?? workspaceId]);
  }, [open, secret, workspaceId]);

  const accountId = useWorkspace(workspaceId).data?.accountId;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId ?? ''),
    queryFn: () => api.accounts.workspaces(accountId!),
    enabled: open && canShare && !!accountId,
  });
  // Ids of deleted workspaces drop out on the next save.
  const chosen = sharedWith.filter((id) => !workspaces.data || workspaces.data.some((w) => w.id === id));
  const workspaceIds = availability === 'workspace' ? [secret?.workspaceId ?? workspaceId] : availability === 'account' ? null : chosen;
  const placementChanged =
    canShare &&
    (availability !== availabilityOf(secret) ||
      (availability === 'selected' && [...chosen].sort().join() !== [...(secret?.sharedWorkspaceIds ?? [])].sort().join()));
  const nameError = name && !NAME.test(name) ? t('Capital letters, digits and _ only, e.g. OPENAI_API_KEY.') : null;
  const sharedError = canShare && availability === 'selected' && !chosen.length ? t('Choose at least one workspace') : null;
  // A credential keeps its stored values while its kind stays: empty means unchanged.
  const keeps = !!secret && secret.kind === kind;
  const filled = pairs.filter((p) => p.key.trim());
  const keyError = filled.some((p) => !KEY.test(p.key))
    ? t('Keys: capital letters, digits and _ only, e.g. HOST.')
    : new Set(filled.map((p) => p.key)).size < filled.length
      ? t('Each key once.')
      : null;
  const known = new Set(keeps ? secret!.keys : []);
  const pairsMissing = !filled.length || filled.some((p) => !p.value && !known.has(p.key));
  const valueError = kind === 'json' ? jsonError(value) : null;
  const pairsChanged = filled.some((p) => p.value) || filled.map((p) => p.key).sort().join() !== [...(secret?.keys ?? [])].sort().join();
  const invalid =
    !name ||
    !!nameError ||
    !!sharedError ||
    !!valueError ||
    (kind === 'key_value' ? !!keyError || pairsMissing : !keeps && !value);

  const save = useApiMutation(
    () => {
      const placement = placementChanged ? { workspaceIds } : {};
      const fields = Object.fromEntries(filled.map((p) => [p.key, p.value]));
      const content =
        kind === 'key_value' ? (!keeps || pairsChanged ? { fields } : {}) : value ? { value } : {};
      return secret
        ? api.secrets.update(secret.id, { name, description, kind, ...content, ...placement }, workspaceId)
        : api.secrets.create(workspaceId, { name, description, kind, ...content, ...placement });
    },
    {
      success: secret ? t('Credential saved') : t('Credential added'),
      onSuccess: () => {
        void invalidateSecrets(queryClient);
        setOpen(false);
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!invalid) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
          <DialogHeader>
            <DialogTitle>{secret ? t('Edit credential') : t('New credential')}</DialogTitle>
            <DialogDescription>
              {t('Stored encrypted and never shown again. Runs on a Claude or Codex subscription get it as an environment variable in their shell.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('Name (environment variable)')} error={nameError}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              placeholder="OPENAI_API_KEY"
              className="font-mono"
              spellCheck={false}
              required
              autoFocus
            />
          </Field>
          <Field
            label={t('Type')}
            hint={
              kind === 'key_value'
                ? t('Runs get each pair as its own variable: {example}.', { example: `${name || 'NAME'}_${filled[0]?.key || 'HOST'}` })
                : kind === 'json'
                  ? t('Runs get the JSON in {name} and as a file whose path is in {name}_FILE.', { name: name || 'NAME' })
                  : t('Runs get the value in {name}.', { name: name || 'NAME' })
            }
          >
            <Select value={kind} onValueChange={(v) => setKind(v as RunSecretKind)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(KINDS) as RunSecretKind[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {KINDS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {kind === 'text' && (
            <Field label={t('Value')}>
              <Input
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoComplete="new-password"
                placeholder={keeps ? t('•••••••• (unchanged)') : ''}
                required={!keeps}
              />
            </Field>
          )}
          {kind === 'json' && (
            <Field label={t('JSON')} error={valueError}>
              <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={6}
                spellCheck={false}
                className="max-h-64 font-mono text-xs"
                placeholder={keeps ? t('•••••••• (unchanged)') : '{ "type": "service_account", … }'}
                required={!keeps}
              />
            </Field>
          )}
          {kind === 'key_value' && (
            <Field label={t('Keys and values')} error={keyError}>
              <div className="grid gap-1.5">
                {pairs.map((p, i) => (
                  <div key={i} className="flex gap-1.5">
                    <Input
                      value={p.key}
                      onChange={(e) => setPairs((all) => all.map((x, j) => (j === i ? { ...x, key: e.target.value.toUpperCase() } : x)))}
                      placeholder="HOST"
                      aria-label={t('Key')}
                      className="w-2/5 font-mono"
                      spellCheck={false}
                    />
                    <Input
                      type="password"
                      value={p.value}
                      onChange={(e) => setPairs((all) => all.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                      autoComplete="new-password"
                      aria-label={t('Value')}
                      placeholder={known.has(p.key) ? t('•••••••• (unchanged)') : t('Value')}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="mt-0.5 shrink-0"
                      aria-label={t('Remove')}
                      title={t('Remove')}
                      disabled={pairs.length === 1}
                      onClick={() => setPairs((all) => all.filter((_, j) => j !== i))}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="justify-self-start" onClick={() => setPairs((all) => [...all, { key: '', value: '' }])}>
                  <Plus /> {t('Add key')}
                </Button>
              </div>
            </Field>
          )}
          <Field label={t('Description')} optional>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('e.g. for pnpm i18n:translate')} />
          </Field>
          {canShare && (
            <>
              <Field
                label={t('Available in')}
                error={sharedError}
                hint={
                  availability === 'workspace'
                    ? t('Only desks in this workspace can give it to their agents.')
                    : t('Desks in these workspaces can give it to their agents. Only account admins can change it.')
                }
              >
                <Select value={availability} onValueChange={(v) => setAvailability(v as Availability)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workspace">{t('This workspace')}</SelectItem>
                    <SelectItem value="account">{t('All workspaces of the account')}</SelectItem>
                    <SelectItem value="selected">{t('Selected workspaces')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {availability === 'selected' && (
                <div className="-mt-2 grid max-h-48 gap-1.5 overflow-y-auto rounded-md border p-2">
                  {workspaces.isLoading && <span className="text-xs text-muted-foreground">{t('Loading workspaces…')}</span>}
                  {workspaces.data?.map((w) => (
                    <label key={w.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={sharedWith.includes(w.id)}
                        onChange={(e) => setSharedWith((ids) => (e.target.checked ? [...ids, w.id] : ids.filter((id) => id !== w.id)))}
                      />
                      <span className="truncate">{w.name}</span>
                      {w.id === workspaceId && <span className="text-xs text-muted-foreground">({t('this workspace')})</span>}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={invalid} loading={save.isPending}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
